/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import LinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageInput from '../../components/ImageInput/ImageInput';
import Wysiwyg from '../../components/Wysiwyg/Wysiwyg';
import Error from '../../components/Error/Error';
import { Context as UserContext } from '../../context/UserContext';
import { Context as ListicleContext } from '../../context/ListicleContext';

import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { api } from '../../api/api';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '26px',
  },
  container: {
    marginTop: theme.spacing(3),
  },
  progress: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(3),
  },
  product: {
    display: 'flex',
    width: '100%',
    marginBottom: theme.spacing(3),
    borderBottom: '1px solid #eee',
    paddingBottom: theme.spacing(2),
  },
  productLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  productIcons: {
    display: 'flex',
    justifyContent: 'center',
  },
  productIcon: {
    alignSelf: 'center',
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  pageActionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  reorderWrap: {
    display: 'block',
    marginBottom: theme.spacing(2),
    maxHeight: 500,
    overflowY: 'auto',
  },
  reorderChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    cursor: 'grab',
    width: '100%',
  },
}));

export default function ListiclePage({ onRender }) {
  // VARS
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();

  // STATE
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // headline handled directly via WYSIWYG bound to listicle
  const [authorName, setAuthorName] = useState('');
  const [authorPhoto, setAuthorPhoto] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [products, setProducts] = useState([]);
  const [logo, setLogo] = useState('');
  const [favicon, setFavicon] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, productId: null });

  // CONTEXTS
  const {
    state: { user },
  } = useContext(UserContext);

  const {
    state: { listicle, loading: listicleLoading, error: listicleError },
    loadListicle,
    updateListicle,
  } = useContext(ListicleContext);

  // EFFECTS
  useEffect(() => {
    if (user) {
      loadListicle(id);
    }
  }, [user, id]);

  // Initialize page state only when listicle id changes (prevents resets on each save)
  useEffect(() => {
    if (listicle && listicle._id) {
      onRender(`Listicle - ${listicle.name}`);
      setName(listicle.name || '');
      setSlug(listicle.slug || '');
      setAuthorName(listicle.authorName || '');
      setAuthorPhoto(listicle.authorPhoto || '');
      setHeroImage(listicle.heroImage || '');
      setLogo(listicle.logo || '');
      setFavicon(listicle.favicon || '');
    } else {
      setName('');
      setSlug('');
      setAuthorName('');
      setAuthorPhoto('');
      setHeroImage('');
      setLogo('');
      setFavicon('');
      setProducts([]);
    }
  }, [listicle?._id]);

  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        const response = await api.get(`/listicles/${listicle._id}/products`);
        setProducts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }

    if (listicle && listicle._id) {
      fetchProducts();
    }
  }, [listicle?._id]);

  if (listicleError) {
    return <Error error={listicleError} />;
  }

  if (!listicle) {
    return null;
  }

  // Confirm delete helpers
  function openConfirmDelete(productId) {
    setConfirmDelete({ open: true, productId });
  }
  function closeConfirmDelete() {
    setConfirmDelete({ open: false, productId: null });
  }

  function handleListicleUpdate(update) {
    updateListicle(id, update);
  }

  function handleWysiwygBlur(name, value) {
    if (listicle && listicle[name] === value) return;
    handleListicleUpdate({ [name]: value });
  }

  function renderPageActionButtons() {
    async function onCloneClick() {
      setLoading(true);

      try {
        const response = await api.post(`/listicles/${listicle._id}/clone`);
        const created = response.data;

        history.push(`/listicles/${created._id}`);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error: ', error);
      } finally {
        setLoading(false);
      }
    }

    return (
      <Box className={classes.pageActionButtons}>
        <Button
          variant='contained'
          color='primary'
          endIcon={<ContentCopyIcon />}
          onClick={onCloneClick}
          disabled={loading}
        >
          Clone
        </Button>
      </Box>
    );
  }

  function renderProducts() {
    // Helper functions

    // Function to update a product
    async function updateProduct(productId, data) {
      try {
        const response = await api.patch(`/listicleProducts/${productId}`, data);
        // Update the product in the local state
        setProducts((prevProducts) =>
          prevProducts.map((product) => (product._id === productId ? response.data : product))
        );
      } catch (error) {
        console.error('Error updating product:', error);
      }
    }

    // Function to add a new product
    async function handleAddProduct() {
      const newProduct = {
        title: '',
        link: '',
        image: '',
        order: products.length + 1,
        ctaText: 'Learn More About',
        description: '',
        rating: 0,
        discount: '',
        stockLevelLow: false,
        urgencyText: '',
        tag: '',
        listicleId: listicle._id,
      };

      try {
        const response = await api.post(`/listicles/${listicle._id}/products`, newProduct);
        setProducts([...products, response.data]);
      } catch (error) {
        console.error('Error adding product:', error);
      }
    }

    // Function to handle local state changes for product fields
    function handleProductChange(e, index) {
      const { name, value } = e.target;
      const updatedProducts = [...products];
      updatedProducts[index] = { ...updatedProducts[index], [name]: value };
      setProducts(updatedProducts);
    }

    // Function to handle updating the product in the backend on blur
    async function handleProductBlur(e, index) {
      const { name, value } = e.target;
      const productId = products[index]._id;

      try {
        if (name === 'order') {
          const newOrder = parseInt(value, 10);
          if (!Number.isFinite(newOrder)) return;
          const sorted = [...products].sort((a, b) => a.order - b.order);
          const items = [...sorted];
          const currentIdx = items.findIndex((p) => p._id === productId);
          if (currentIdx === -1) return;
          const [moved] = items.splice(currentIdx, 1);
          items.splice(newOrder - 1, 0, moved);
          const reindexed = reindexOrders(items);
          await persistOrders(reindexed);
        } else {
          await updateProduct(productId, { [name]: value });
        }
      } catch (error) {
        console.error('Error updating product:', error);
      }
    }

    // Function to handle Wysiwyg editor blur events
    async function handleWysiwygBlur(index, fieldName, value) {
      const productId = products[index]._id;
      if (products[index][fieldName] === value) return;
      const updatedProducts = [...products];
      updatedProducts[index] = { ...updatedProducts[index], [fieldName]: value };
      setProducts(updatedProducts);

      try {
        await updateProduct(productId, { [fieldName]: value });
      } catch (error) {
        console.error('Error updating product:', error);
      }
    }

    // Function to handle boolean checkbox changes
    async function handleProductCheckboxChange(e, index) {
      const { name, checked } = e.target;
      const productId = products[index]._id;
      const updatedProducts = [...products];
      updatedProducts[index] = { ...updatedProducts[index], [name]: checked };
      setProducts(updatedProducts);

      try {
        await updateProduct(productId, { [name]: checked });
      } catch (error) {
        console.error('Error updating product:', error);
      }
    }

    // Function to delete a product
    async function handleDeleteProduct(productId) {
      try {
        await api.delete(`/listicleProducts/${productId}`);
        setProducts((prevProducts) => prevProducts.filter((product) => product._id !== productId));
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }

    // Reorder helpers
    const reindexOrders = (items) => items.map((p, idx) => ({ ...p, order: idx + 1 }));

    async function persistOrders(updatedList) {
      try {
        setLoading(true);
        const changes = updatedList.filter((p) => {
          const original = products.find((x) => x._id === p._id);
          return original && original.order !== p.order;
        });
        if (changes.length === 0) {
          setProducts(updatedList);
          return;
        }
        await Promise.all(changes.map((p) => api.patch(`/listicleProducts/${p._id}`, { order: p.order })));
        setProducts(updatedList);
      } catch (error) {
        console.error('Error persisting product order:', error);
      } finally {
        setLoading(false);
      }
    }

    function stripHtml(html = '') {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }

    function shortLabel(product) {
      const raw = stripHtml(product.title) || product.link || `#${product.order}`;
      const words = (raw || '').trim().split(/\s+/).filter(Boolean);
      const preview = words.slice(0, 4).join(' '); // first couple of words
      return preview || raw;
    }

    function onDragEnd(result) {
      if (!result.destination) return;
      const sorted = [...products].sort((a, b) => a.order - b.order);
      const items = [...sorted];
      const [removed] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, removed);
      const reindexed = reindexOrders(items);
      persistOrders(reindexed);
    }

    // Render logic
    if (!products.length) {
      return (
        <>
          <Typography className={classes.title} variant='h6' gutterBottom>
            Products
          </Typography>
          <IconButton
            size='small'
            color='primary'
            aria-label='Add'
            component='span'
            onClick={handleAddProduct}
            className={classes.productIcon}
          >
            <AddIcon />
          </IconButton>
        </>
      );
    }

    const sortedProducts = [...products].sort((a, b) => a.order - b.order);
    const orderSelectOptions = Array.from({ length: products.length }, (_, i) => i + 1);

    return (
      <>
        <Typography className={classes.title} variant='h6' gutterBottom>
          Products
        </Typography>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='reorderChips' direction='vertical'>
            {(provided) => (
              <Box className={classes.reorderWrap} ref={provided.innerRef} {...provided.droppableProps}>
                {sortedProducts.map((p, idx) => (
                  <Draggable key={p._id} draggableId={p._id} index={idx}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <Chip
                          className={classes.reorderChip}
                          color='default'
                          variant='outlined'
                          label={`${idx + 1}. ${shortLabel(p)}`}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        {sortedProducts.map((product, index) => {
          const {
            _id,
            title,
            link,
            image,
            order,
            ctaText,
            description,
            rating,
            discount,
            stockLevelLow,
            urgencyText,
            tag,
          } = product;

          return (
            <Box className={classes.product} key={_id}>
              <Box className={classes.productLeft}>
                <Typography variant='h6'>Title</Typography>
                <Wysiwyg
                  key={`title-${_id}`}
                  height={50}
                  initialValue={title}
                  onBlur={(value) => handleWysiwygBlur(index, 'title', value)}
                />
                <TextField
                  name='link'
                  label='Link'
                  value={link}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <FormControl className={classes.textField}>
                  <InputLabel>Order</InputLabel>
                  <Select
                    name='order'
                    value={order}
                    label='Order'
                    onChange={(e) => handleProductChange(e, index)}
                    onBlur={(e) => handleProductBlur(e, index)}
                  >
                    {orderSelectOptions.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  name='ctaText'
                  label='CTA Text'
                  value={ctaText}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <TextField
                  name='tag'
                  label='Tag (optional)'
                  placeholder='e.g., Under $XX'
                  value={tag || ''}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <TextField
                  name='urgencyText'
                  label='Urgency text (optional)'
                  placeholder='e.g., 🔥 Only 14 left at this price'
                  value={urgencyText || ''}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                  multiline
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name='stockLevelLow'
                      color='primary'
                      checked={Boolean(stockLevelLow)}
                      onChange={(e) => handleProductCheckboxChange(e, index)}
                    />
                  }
                  label='Stock level low'
                />
                <TextField
                  name='rating'
                  label='Rating'
                  type='number'
                  inputProps={{ step: 0.1, min: 0, max: 5 }}
                  value={rating ?? ''}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <TextField
                  name='discount'
                  label='Discount % or Amount'
                  placeholder='e.g., 20% or $4.99'
                  value={discount || ''}
                  onChange={(e) => handleProductChange(e, index)}
                  className={classes.textField}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <ImageInput
                  name='image'
                  label='Image'
                  value={image}
                  className={classes.textField}
                  onChange={(e) => handleProductChange(e, index)}
                  onBlur={(e) => handleProductBlur(e, index)}
                />
                <Typography variant='h6'>Description</Typography>
                <Wysiwyg
                  key={`desc-${_id}`}
                  height={200}
                  initialValue={description}
                  onBlur={(value) => handleWysiwygBlur(index, 'description', value)}
                />
              </Box>
              <Box className={classes.productIcons}>
                <IconButton
                  color='secondary'
                  aria-label='delete product'
                  component='span'
                  size='small'
                  className={classes.productIcon}
                  onClick={() => openConfirmDelete(_id)}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  size='small'
                  color='primary'
                  aria-label='Add'
                  component='span'
                  onClick={handleAddProduct}
                  className={classes.productIcon}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          );
        })}
        <Box display='flex' justifyContent='center' mt={2} mb={2}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleAddProduct}
            disabled={loading}
            startIcon={<AddIcon />}
          >
            Add new product
          </Button>
        </Box>

        {/* Confirmation Modal for deleting a product */}
        <Dialog open={confirmDelete.open} onClose={closeConfirmDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this product? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirmDelete} color='primary'>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDelete.productId) {
                  handleDeleteProduct(confirmDelete.productId);
                }
                closeConfirmDelete();
              }}
              color='secondary'
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Container maxWidth='lg' className={classes.container}>
      {(listicleLoading || loading) && <LinearProgress className={classes.progress} />}
      {renderPageActionButtons()}
      <Paper className={classes.paper}>
        <TextField
          label='Id'
          value={listicle._id}
          fullWidth
          InputProps={{
            readOnly: true,
          }}
          className={classes.textField}
        />
        <TextField
          label='Name'
          helperText='Inner name for admin purposes. Not visible to the user.'
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              name,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <TextField
          label='Slug'
          helperText='URL slug for the listicle.'
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              slug,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <ImageInput
          label='Logo'
          value={logo}
          onChange={(e) => setLogo(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              logo,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <ImageInput
          label='Favicon'
          value={favicon}
          onChange={(e) => setFavicon(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              favicon,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <Typography className={classes.title}>Headline</Typography>
        <Wysiwyg
          key={`headline-${listicle._id}`}
          height={100}
          initialValue={listicle.headline}
          onBlur={(value) => handleWysiwygBlur('headline', value)}
        />
        <TextField
          label='Author Name'
          value={authorName}
          onChange={(e) => setAuthorName(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              authorName,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <ImageInput
          label='Author Photo'
          value={authorPhoto}
          onChange={(e) => setAuthorPhoto(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              authorPhoto,
            })
          }
          fullWidth
          className={classes.textField}
        />
        <ImageInput
          label='Hero Image'
          value={heroImage}
          onChange={(e) => setHeroImage(e.target.value)}
          onBlur={() =>
            handleListicleUpdate({
              heroImage,
            })
          }
          fullWidth
          className={classes.textField}
        />

        <Typography className={classes.title}>Hero Text</Typography>
        <Wysiwyg
          key={`heroText-${listicle._id}`}
          height={300}
          initialValue={listicle.heroText}
          onBlur={(value) => handleWysiwygBlur('heroText', value)}
        />
        <Typography className={classes.title}>Update text</Typography>
        <Wysiwyg
          key={`updateText-${listicle._id}`}
          height={200}
          initialValue={listicle.updateText}
          onBlur={(value) => handleWysiwygBlur('updateText', value)}
        />

        {renderProducts()}
      </Paper>
    </Container>
  );
}
